import React, { useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import { useLocation, useNavigate } from 'react-router-dom';
import { APISessionTransferExchange } from '../restAPI/PostAPI';
import {
    AGENCY_TIMEZONE_COOKIE,
    resolveAgencyTimezone,
} from '../function/agencyTimezone';

const getExchangeData = (result) => result?.data?.data || result?.data || {};

const getJwtFromExchangeResponse = (result) => {
    const exchangeData = getExchangeData(result);
    const maybeJwt = exchangeData?.jwt || exchangeData?.tokens;

    return typeof maybeJwt === 'string' ? maybeJwt : null;
};

const getAgencyUuidFromExchangeResponse = (result) => {
    const exchangeData = getExchangeData(result);
    const maybeAgencyUuid = exchangeData?.agencyUuid;

    return typeof maybeAgencyUuid === 'string' ? maybeAgencyUuid : null;
};

const getExchangeMetadata = (result) => {
    const exchangeData = getExchangeData(result);
    // eslint-disable-next-line no-underscore-dangle
    const userId = exchangeData?._id;

    return {
        agencyId: exchangeData?.agencyId,
        userId,
        role: exchangeData?.role,
        agencyName: exchangeData?.agencyName,
        userFirstName: exchangeData?.userFirstName,
        userLastName: exchangeData?.userLastName,
        profileImageUrl: exchangeData?.profileImageUrl,
        listAgency: exchangeData?.listAgency,
    };
};

export default function SessionTransferDashboardGate({ children, sessionTransferTargetPath = '/dashboard' }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [done, setDone] = useState(false);
    const lastProcessedToken = useRef(null);

    const queryToken = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('token');
    }, [location.search]);

    useEffect(() => {
        const run = async () => {
            if (!queryToken) {
                setDone(true);
                return;
            }

            try {
                // Clear existing session data to ensure a clean state
                [
                    'jwtToken', 'agencyUUID', 'agency-id', '_id', 'role', 'agency-name',
                    'name', 'profileImageUrl', 'SessionID', 'privileges', 'timezone',
                    AGENCY_TIMEZONE_COOKIE,
                ].forEach((c) => Cookies.remove(c));
                ['listAgency', 'alertPreferences'].forEach((l) => localStorage.removeItem(l));

                setDone(false);
                const envBase = process.env.REACT_APP_SESSION_TRANSFER_REDIRECT_URL?.trim();
                const base = (envBase && envBase !== '')
                    ? envBase.replace(/\/$/, '')
                    : window.location.origin;
                const targetPath = sessionTransferTargetPath.startsWith('/') ? sessionTransferTargetPath : '/' + sessionTransferTargetPath;
                const redirectUrl = base + targetPath;
                const result = await APISessionTransferExchange(queryToken, redirectUrl);
                const jwt = getJwtFromExchangeResponse(result);
                const agencyUuid = getAgencyUuidFromExchangeResponse(result);
                const {
                    agencyId,
                    userId,
                    role,
                    agencyName,
                    userFirstName,
                    userLastName,
                    profileImageUrl,
                    listAgency,
                } = getExchangeMetadata(result);

                if (jwt && agencyUuid) {
                    try {
                        const decoded = jwtDecode(jwt);
                        const expMs = typeof decoded?.exp === 'number' ? decoded.exp * 1000 : null;
                        const expires = expMs ? new Date(expMs) : undefined;
                        const cookieOptions = expires ? { expires } : undefined;
                        Cookies.set('jwtToken', jwt, cookieOptions);
                        Cookies.set('agencyUUID', agencyUuid, cookieOptions);
                        if (agencyId) Cookies.set('agency-id', agencyId, cookieOptions);
                        if (userId) Cookies.set('_id', userId, cookieOptions);
                        if (typeof role === 'string' && role.length > 0) {
                            Cookies.set('role', role.replace(/_/g, ' '), cookieOptions);
                        }
                        if (agencyName) Cookies.set('agency-name', agencyName, cookieOptions);
                        if (userFirstName || userLastName) {
                            const fullName = [userFirstName, userLastName].filter(Boolean).join(' ');
                            Cookies.set('name', fullName, cookieOptions);
                        }
                        if (profileImageUrl) Cookies.set('profileImageUrl', profileImageUrl, cookieOptions);
                        if (Array.isArray(listAgency)) {
                            localStorage.setItem('listAgency', JSON.stringify(listAgency));
                        }
                        const agencyTimezone = resolveAgencyTimezone({
                            privileges: listAgency,
                            agencyUuid,
                            agencyId,
                        });
                        if (agencyTimezone) {
                            Cookies.set(AGENCY_TIMEZONE_COOKIE, agencyTimezone, cookieOptions);
                        }
                    } catch (e) {
                        // The JWT is invalid, do not store it. Redirect to login.
                        console.error('Invalid JWT received during session transfer:', e);
                        navigate('/login', { replace: true });
                        return;
                    }
                    navigate(sessionTransferTargetPath, { replace: true });
                    return;
                }
                navigate('/login', { replace: true });
            } catch (e) {
                console.error('Session transfer exchange failed:', e);
                navigate('/login', { replace: true });
            }
        };

        if (queryToken && queryToken !== lastProcessedToken.current) {
            lastProcessedToken.current = queryToken;
            run();
        } else if (!queryToken) {
            run();
        }
    }, [queryToken, navigate, sessionTransferTargetPath]);

    if (!done && queryToken) return null;
    return children;
}
